import { useEffect, useRef, useState } from 'react'
import { SignInManager } from '../SignInManager'
import { customAmplitude } from '@/utils/customAmplitude'
import { captureException } from '@sentry/react'
import {
  buildSignUpClickProperties,
  buildSignupAnalyticsContext,
  type SignupAnalyticsContext,
} from '@/utils/signupPremiumAnalytics'

export type SocialProvider = 'google' | 'facebook' | 'apple'

const SOCIAL_LOGIN_TIMEOUT_MS = 30000

const getErrorDetails = (error: unknown): { errorCode: string; errorMessage: string } => {
  if (error instanceof Error) {
    const maybeCode = error as Error & { code?: string }
    return {
      errorCode: maybeCode.code ?? 'error',
      errorMessage: error.message || 'Social login error',
    }
  }

  if (typeof error === 'string') {
    return { errorCode: 'error', errorMessage: error }
  }

  return { errorCode: 'unknown', errorMessage: 'Social login error' }
}

const withTimeout = <T,>(promise: Promise<T>, social: SocialProvider) =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(`Social login timed out for ${social}`))
    }, SOCIAL_LOGIN_TIMEOUT_MS)

    promise
      .then((value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      })
  })

interface UseSocialLoginOptions {
  onSuccess?: (user: any) => void
  onError?: (error: Error, social: SocialProvider) => void
  getAnalyticsContext?: () => SignupAnalyticsContext
}

export function useSocialLogin(options: UseSocialLoginOptions = {}) {
  const [loadingSocial, setLoadingSocial] = useState<SocialProvider | null>(null)
  const [socialError, setSocialError] = useState<SocialProvider | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const login = async (social: SocialProvider) => {
    if (loadingSocial) {
      return
    }

    setLoadingSocial(social)
    setSocialError(null)
    let user

    try {
      const analyticsContext = options.getAnalyticsContext?.()
      const signUpClickPayload = buildSignUpClickProperties({
        context:
          analyticsContext ??
          buildSignupAnalyticsContext({
            source: null,
            type: 'Standard',
          }),
        destination: social,
      })

      customAmplitude('Sign up click', signUpClickPayload)

      if (social === 'facebook') {
        user = await withTimeout(SignInManager.getInstance().loginWithFacebook(), social)
      } else if (social === 'google') {
        user = await withTimeout(SignInManager.getInstance().loginWithGoogle(), social)
      } else if (social === 'apple') {
        user = await withTimeout(SignInManager.getInstance().loginWithApple(), social)
      }
    } catch (error) {
      const { errorCode, errorMessage } = getErrorDetails(error)
      captureException(error, {
        level: 'fatal',
        extra: { customMessage: 'Social login error' },
        tags: { module: 'login' },
      })
      customAmplitude('Sign up error', {
        ...buildSignUpClickProperties({
          context:
            options.getAnalyticsContext?.() ??
            buildSignupAnalyticsContext({
              source: null,
              type: 'Standard',
            }),
          destination: social,
        }),
        error: 'Social login error',
        error_code: errorCode,
        error_message: errorMessage,
        'Auth Flow': 'SignIn',
      })
      if (!isMountedRef.current) {
        return
      }
      setLoadingSocial(null)
      setSocialError(social)
      options.onError?.(error as Error, social)
      return
    }

    // Post-login handling - outside try-catch to avoid misreporting errors
    if (!isMountedRef.current) {
      return
    }
    setLoadingSocial(null)

    // A null/undefined user means the user canceled or aborted the provider
    // flow (e.g. closed the Facebook/Google/Apple popup) without authenticating.
    // Do not treat this as success - keep the sign-up screen visible instead of
    // letting the user into the app unauthenticated.
    if (!user) {
      return
    }

    options.onSuccess?.(user)
  }

  return { login, loadingSocial, socialError }
}
